import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  ChevronRight,
  Receipt,
  Search,
  X,
} from "lucide-react";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import { AppDispatch, RootState } from "../../../../store";
import { getWalletTransactions } from "../../../../Redux/wallet_apis/slice";
import {
  UserType,
  WalletTransaction,
  WalletTransactionQuery,
} from "../../../../Redux/wallet_apis/types";
import "../WalletHistory.css";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const statusClass = (status?: string) =>
  `wallet-hist-status wallet-hist-status-${(status || "pending")
    .toLowerCase()
    .replace(/[^a-z]+/g, "")}`;

const describeTransaction = (t: WalletTransaction) => {
  const cp = t.counterparty;
  if (cp) {
    const who = cp.name
      ? `${cp.name}${cp.registerId ? ` (${cp.registerId})` : ""}`
      : cp.registerId || cp.userId;
    return `Fund transfer ${t.type === "DEBIT" ? "to" : "from"} ${who}`;
  }
  return t.description || "";
};

const CreditDebit = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { transactions, loading, pagination } = useSelector(
    (state: RootState) => state.wallet,
  );

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    const query: WalletTransactionQuery = {
      page,
      limit,
      sortOrder: "DESC",
      userType: UserType.CUSTOMER,
      startDate: dateRange.from || undefined,
      endDate: dateRange.to || undefined,
    };
    dispatch(getWalletTransactions(query));
  }, [dispatch, page, limit, dateRange.from, dateRange.to]);

  const records = (transactions || []).filter((t) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      t.transactionNumber?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q)
    );
  });

  const totalRecords = pagination?.total ?? records.length;

  return (
    <section className="wallet-hist-page">
      <div className="wallet-hist-shell">
        <header className="wallet-hist-header">
          <div className="wallet-hist-header-left">
            <div className="wallet-hist-title-icon">
              <Receipt size={22} strokeWidth={2} />
            </div>
            <div>
              <span className="wallet-hist-eyebrow">MY WALLET</span>
              <h1>Credit / Debit</h1>
              <p>Every wallet transaction in one clean ledger view.</p>
            </div>
          </div>
          <span className="wallet-hist-count-pill">
            {totalRecords} records
          </span>
        </header>

        <div className="wallet-hist-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="wallet-hist-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <div className="wallet-hist-search">
            <Search size={16} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search transactions..."
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : (
          <>
            <div className="wallet-hist-card-list is-desktop-list">
              {records.map((transaction) => (
                <article
                  className="wallet-hist-card"
                  key={transaction.id}
                >
                  <div className="wallet-hist-card-meta">
                    <div>
                      <span>Date</span>
                      <strong>
                        {new Date(transaction.createdAt).toLocaleDateString(
                          "en-GB",
                          { day: "2-digit", month: "short", year: "numeric" },
                        )}
                      </strong>
                    </div>
                    <div>
                      <span>Transaction No</span>
                      <strong>{transaction.transactionNumber}</strong>
                    </div>
                    <div>
                      <span>Type</span>
                      <span className={statusClass(transaction.type)}>
                        {transaction.type}
                      </span>
                    </div>
                    <div className="wallet-hist-card-status">
                      <span>Status</span>
                      <span className={statusClass(transaction.status)}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                  <div className="wallet-hist-card-body">
                    <p className="wallet-hist-desc">
                      {describeTransaction(transaction) || "N/A"}
                    </p>
                    <div
                      className={`wallet-hist-amount is-${transaction.type === "CREDIT" ? "credit" : "debit"}`}
                    >
                      <small>{transaction.type}</small>
                      <strong>{formatCurrency(transaction.amount)}</strong>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="wallet-hist-mobile-list">
              {records.map((transaction) => (
                <div className="wallet-hist-mobile-card" key={transaction.id}>
                  <span
                    className={`wallet-hist-mobile-icon is-${transaction.type === "CREDIT" ? "credit" : "debit"}`}
                  >
                    {transaction.type === "CREDIT" ? (
                      <ArrowDownLeft size={19} />
                    ) : (
                      <ArrowUpRight size={19} />
                    )}
                  </span>
                  <span className="wallet-hist-mobile-copy">
                    <span className="wallet-hist-mobile-topline">
                      <span className={statusClass(transaction.status)}>
                        {transaction.status}
                      </span>
                      <span className="wallet-hist-mobile-date">
                        <CalendarDays size={13} />
                        {new Date(transaction.createdAt).toLocaleDateString(
                          "en-GB",
                          { day: "2-digit", month: "short", year: "numeric" },
                        )}
                      </span>
                    </span>
                    <strong className="wallet-hist-mobile-title">
                      {describeTransaction(transaction) || "Wallet Transaction"}
                    </strong>
                    <span className="wallet-hist-mobile-desc">
                      {transaction.transactionNumber}
                    </span>
                    <span className="wallet-hist-mobile-bottomline">
                      <strong>{formatCurrency(transaction.amount)}</strong>
                      <span>{transaction.type}</span>
                    </span>
                  </span>
                  <ChevronRight
                    size={17}
                    className="wallet-hist-mobile-chevron"
                  />
                </div>
              ))}
            </div>

            {records.length === 0 && (
              <div className="wallet-hist-empty">
                <Receipt size={28} />
                <strong>No transactions found</strong>
                <span>Change the search or selected date range.</span>
              </div>
            )}
          </>
        )}

        <div className="wallet-hist-pagination">
          {pagination && (
            <Pagenation
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default CreditDebit;
